import React, { forwardRef, memo, useCallback, useState } from 'react';
import { tintFor } from '../../config/colors';
import { MESSAGE_TYPE } from '../../config/constants';
import { logoImage } from '../../assets/images';
import { useIsTouch } from '../../hooks/useMediaQuery';
import { useImageFade } from '../../hooks/useImageFade';
import PlaceholderAvatar from './PlaceholderAvatar';
import SidebarOverflow from '../Layout/SidebarOverflow';

/**
 * Conversation list primitives shared by the Messages, Group and Calls
 * sidebars. Presentation only — every row is a controlled element driven by
 * the props its container already had.
 *
 * Style objects live at module scope. Declared inline they were rebuilt on
 * every render of every row, and a new object identity also defeats the memo
 * on the rows below.
 */

export const initialsFor = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const titleCase = (value = '') =>
  String(value).replace(/\b\w/g, (char) => char.toUpperCase());

/** Enter/Space activation for the div-based rows, which are not real buttons. */
const onActivateKey = (handler) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler?.(event);
  }
};

/**
 * Preview line for a conversation row — the last message, the way every chat
 * client shows it.
 *
 * The list endpoint already returns it as `chats[0]`, so this is presentation
 * only. Returns null when there is nothing to show so the caller can pick its
 * own fallback (presence for a chat, "Group" for a group).
 *
 * A group prefixes the sender, since the row title names the group rather than
 * whoever spoke; a 1:1 row doesn't, because the title is already that person.
 * Secret groups show the member's anonymous handle, never their real name.
 */
export const messagePreview = (conversation, { isGroup = false, meId, isSecret = false } = {}) => {
  const last = conversation?.chats?.[0];
  if (!last) return null;
  if (last.deleted_by_id) return 'Message deleted';

  const body =
    last.type === MESSAGE_TYPE.MEDIA
      ? 'Media'
      : last.type === MESSAGE_TYPE.LOCATION
        ? 'Location'
        : last.message?.trim();
  if (!body) return null;

  if (meId && String(last.sender_id) === String(meId)) return `You: ${body}`;
  if (!isGroup) return body;

  const sender = isSecret ? last.user?.member_mode || 'Anonymous' : last.user?.first_name;
  return sender ? `${titleCase(sender)}: ${body}` : body;
};

const avatarStyles = {
  wrap: { position: 'relative', flex: '0 0 auto', display: 'inline-flex' },
  dot: {
    position: 'absolute',
    right: -1,
    bottom: -1,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: 'var(--ok)',
    border: '2.5px solid var(--panel)',
  },
};

export const Avatar = memo(function Avatar({
  name,
  seed,
  src,
  online,
  size = 40,
  radius = 'var(--r-avatar)',
}) {
  const fade = useImageFade();

  // No picture AND nothing to build initials from. `initialsFor` answers '?' in
  // that case, so the row used to show a question mark on a hash-picked colour —
  // a confident-looking tint for an account we know nothing about.
  const nameless = !String(name ?? '').trim();

  return (
    <span style={avatarStyles.wrap}>
      {!src && nameless ? (
        <PlaceholderAvatar size={size} radius={radius} />
      ) : src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          {...fade}
          style={{
            width: size,
            height: size,
            borderRadius: radius,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <span
          style={{
            width: size,
            height: size,
            borderRadius: radius,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: size <= 32 ? 11 : size <= 44 ? 13.5 : Math.round(size * 0.32),
            color: '#fff',
            background: tintFor(seed ?? name),
          }}
        >
          {initialsFor(name)}
        </span>
      )}
      {online && <span style={avatarStyles.dot} />}
    </span>
  );
});

const unreadBadgeStyle = {
  flex: '0 0 auto',
  minWidth: 19,
  height: 19,
  padding: '0 6px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--accent)',
  color: 'var(--accent-fg)',
  fontSize: 10.5,
  fontWeight: 800,
  lineHeight: 1,
  display: 'grid',
  placeItems: 'center',
};

export const UnreadBadge = ({ count }) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (
    <span
      // `key` on the count is what makes the pop work: React remounts the node
      // when the number changes, and a CSS animation only restarts on mount.
      // Without it the badge would animate once and then silently tick upward.
      key={n}
      className="badge-pop"
      style={unreadBadgeStyle}
      aria-label={`${n} unread`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
};

const rowStyles = {
  base: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    padding: '9px 10px',
    borderRadius: 'var(--r-lg)',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.13s ease',
  },
  body: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  titleLine: { display: 'flex', alignItems: 'baseline', gap: 8 },
  previewLine: { display: 'flex', alignItems: 'center', gap: 7 },
  truncate: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deleteBtn: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // A 16px glyph is a 16px target. Padded out to a thumb-sized box, with the
    // padding pulled back off the row so the layout is unchanged.
    padding: 8,
    margin: -8,
    border: 0,
    background: 'transparent',
    color: 'var(--danger)',
    cursor: 'pointer',
  },
};

/**
 * A single conversation row.
 *
 * `onActivate` fires on click and on Enter/Space so the row stays reachable
 * by keyboard now that it is a div rather than a link.
 *
 * Hover is owned here rather than by the list. Held in the parent as a
 * `hoveredId`, every mouse move between rows re-rendered the whole list —
 * O(n) work for a background colour on one row. Memoised so a sibling's
 * hover, or an unrelated parent state change, costs nothing.
 *
 * `tone: 'danger'` tints the title and preview — a missed call in the calls
 * list, which is the only row that needs to read as an alert.
 */
export const ConversationRow = memo(function ConversationRow({
  name,
  seed,
  avatarSrc,
  preview,
  time,
  unread,
  online,
  active,
  tone,
  onActivate,
  onDelete,
  DeleteIcon,
}) {
  const [hovered, setHovered] = useState(false);
  const isTouch = useIsTouch();
  const danger = tone === 'danger';

  // Revealing Delete on hover hides it completely from a touch device, where
  // there is no hover to reveal it with — deleting a conversation was
  // unreachable on a phone. On a coarse pointer it simply stays out.
  const deleteVisible = isTouch || hovered;

  const handleDelete = useCallback(
    (event) => {
      event.stopPropagation();
      onDelete?.(event);
    },
    [onDelete]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={active ? 'true' : undefined}
      // The row is styled inline; this class exists purely so the motion layer
      // (config/theme/motion.css) can own its entry, press and focus states.
      className="syn-row"
      onClick={onActivate}
      onKeyDown={onActivateKey(onActivate)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...rowStyles.base,
        background: active ? 'var(--accent-soft)' : hovered ? 'var(--panel-2)' : 'transparent',
        boxShadow: active ? 'inset 0 0 0 1.5px var(--accent)' : 'none',
      }}
    >
      <Avatar name={name} seed={seed} src={avatarSrc} online={online} />

      <span style={rowStyles.body}>
        <span style={rowStyles.titleLine}>
          <span
            style={{
              ...rowStyles.truncate,
              flex: 1,
              minWidth: 0,
              fontWeight: active ? 800 : 600,
              fontSize: 13.5,
              letterSpacing: '-0.01em',
              color: danger ? 'var(--danger)' : 'var(--text)',
            }}
          >
            {name}
          </span>
          {time && (
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
                color: active ? 'var(--accent-strong)' : 'var(--faint)',
                flex: '0 0 auto',
              }}
            >
              {time}
            </span>
          )}
        </span>

        <span style={rowStyles.previewLine}>
          <span
            style={{
              ...rowStyles.truncate,
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              color: danger ? 'var(--danger)' : 'var(--muted)',
            }}
          >
            {preview}
          </span>

          {/* A real button, so deleting is reachable by keyboard. Kept mounted
              but hidden on hover-out — unmounting it on mouseleave dropped the
              focus ring mid-interaction. */}
          {onDelete && DeleteIcon && (
            <button
              type="button"
              title="Delete"
              aria-label={`Delete conversation with ${name}`}
              onClick={handleDelete}
              style={{ ...rowStyles.deleteBtn, visibility: deleteVisible ? 'visible' : 'hidden' }}
            >
              <DeleteIcon size={16} />
            </button>
          )}

          <UnreadBadge count={unread} />
        </span>
      </span>
    </div>
  );
});

const sectionHeaderStyles = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 9px 6px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  label: {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: 'var(--faint)',
  },
  rule: { flex: 1, height: 1, background: 'var(--border)' },
};

export const SectionHeader = ({ label, open, onToggle, Chevron }) => (
  <div
    role="button"
    tabIndex={0}
    aria-expanded={Boolean(open)}
    onClick={onToggle}
    onKeyDown={onActivateKey(onToggle)}
    style={sectionHeaderStyles.wrap}
  >
    <span style={sectionHeaderStyles.label}>{label}</span>
    <span style={sectionHeaderStyles.rule} />
    {Chevron && <Chevron style={{ color: 'var(--faint)' }} />}
  </div>
);

const headerStyles = {
  wrap: {
    flex: '0 0 auto',
    padding: '18px 16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  row: { display: 'flex', alignItems: 'center', gap: 9, minHeight: 34 },
  titleBlock: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  // The section name sits UNDER the wordmark, so it reads as a subtitle rather
  // than competing with it — smaller than the old 19px title, but still the
  // strongest text in the block.
  section: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: 'var(--muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

/**
 * The wordmark-over-section-name row on its own, with an optional trailing
 * action.
 *
 * Split out of `SidebarHeader` so the Notes sidebar can take the brand block
 * without also taking the header's padding and gap — Notes owns its own head
 * element, which already spaces the tab and tag rows underneath. Keeping one
 * definition is the point: Notes had grown its own icon-and-bold-title header,
 * so the section that shipped last was the one section that did not identify
 * the product.
 */
/**
 * Sidebar header: the Synapse wordmark over the name of the section, plus an
 * optional trailing action.
 *
 * The brand is the primary line and the section ("Messages", "Calls") reads as
 * its subtitle. On a phone the sidebar IS the screen, so this is the only place
 * the product identifies itself — the rail that carries the brand on desktop
 * collapses to an icon island there, with no room for a wordmark.
 *
 * A hamburger used to sit at the end of this row on mobile. It toggled the rail
 * off-canvas, which stopped being a thing the moment the rail became that
 * island — the island is always on screen, so the control had nothing left to
 * open. It was removed rather than hidden: a header button that does nothing is
 * worse than no button.
 */
export const SidebarBrand = ({ title, action }) => (
  <div style={headerStyles.row}>
    <div style={headerStyles.titleBlock}>
      <img className="sidebar-wordmark" src={logoImage} alt="Synapse" />
      {title && <div style={headerStyles.section}>{title}</div>}
    </div>
    {action}
    {/* Phone only, and it returns null above that width — see SidebarOverflow. Rendered
        here rather than passed in by each sidebar so all six list headers carry it from
        one place; the cost is that this module, which is otherwise pure presentation,
        now pulls in one connected component. */}
    <SidebarOverflow />
  </div>
);

export const SidebarHeader = ({ title, action, children }) => (
  <div style={headerStyles.wrap}>
    <SidebarBrand title={title} action={action} />
    {children}
  </div>
);

const tabPillStyle = {
  // Above the track's sliding indicator, and transparent so the indicator is
  // what paints the selected surface. A pill that painted its own background
  // would simply cover the thing that is supposed to travel underneath it.
  position: 'relative',
  zIndex: 1,
  flex: 1,
  height: 30,
  border: 0,
  background: 'transparent',
  borderRadius: 7,
  fontSize: 12.5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  transition: 'color 0.14s ease',
};

/**
 * One segment of the filter control. Reads as a raised tab on the recessed
 * track when selected, and as plain muted text when not — the previous
 * treatment inverted the whole pill to `--text`, which was the highest-contrast
 * thing in the column and pulled focus off the conversation list itself.
 */
export const TabPill = ({ active, children, style, ...rest }) => (
  <button
    type="button"
    aria-pressed={Boolean(active)}
    // Hook for the motion layer's press feedback; the styling stays inline.
    className="tab-pill"
    {...rest}
    style={{
      ...tabPillStyle,
      color: active ? 'var(--text)' : 'var(--muted)',
      fontWeight: active ? 800 : 600,
      ...style,
    }}
  >
    {children}
  </button>
);

/** Count that rides inside a segment, e.g. "Unread 3". */
export const TabCount = ({ children }) => (
  <span style={{ fontSize: 10.5, fontWeight: 800, fontFamily: 'var(--font-mono)', opacity: 0.75 }}>
    {children}
  </span>
);

/* Track geometry. The sliding indicator's width and travel are computed from
   these, so a change to either stays consistent with the pills. */
const TAB_GAP = 5;
const TAB_PAD = 3;

const tabRowStyle = {
  position: 'relative',
  display: 'flex',
  gap: TAB_GAP,
  padding: TAB_PAD,
  borderRadius: 'var(--r-md)',
  background: 'var(--panel-2)',
  border: '1px solid var(--border)',
};

/* The raised surface behind the selected segment. It lives on the track, not on
   the pill, which is what lets it travel between segments instead of blinking
   out of one and into another. */
const tabIndicatorStyle = {
  position: 'absolute',
  top: TAB_PAD,
  bottom: TAB_PAD,
  left: TAB_PAD,
  borderRadius: 7,
  background: 'var(--panel)',
  boxShadow: 'var(--shadow)',
  pointerEvents: 'none',
};

/**
 * Segmented track the filter pills sit in. `flush` drops the outer gutter for
 * the sidebars that render it inside the header block, which already has one.
 *
 * The active segment is read off the children rather than passed in, so all
 * three call sites keep rendering `<TabPill active={…}>` exactly as before and
 * none of them has to also hand over an index that could disagree with it.
 */
export const TabRow = ({ children, style, flush = false }) => {
  const segments = React.Children.toArray(children);
  const activeIndex = segments.findIndex((segment) => segment?.props?.active);
  const count = segments.length || 1;

  return (
    <div style={{ ...(flush ? null : { margin: '0 16px 12px' }), ...tabRowStyle, ...style }}>
      {activeIndex >= 0 && (
        <span
          className="tab-indicator"
          aria-hidden="true"
          style={{
            ...tabIndicatorStyle,
            // Percentages inside `width` resolve against the track; inside
            // `translateX` they resolve against the indicator's own box — which
            // is exactly one segment, so a step is "my width plus one gap".
            width: `calc((100% - ${TAB_PAD * 2}px - ${(count - 1) * TAB_GAP}px) / ${count})`,
            transform: `translateX(calc(${activeIndex} * (100% + ${TAB_GAP}px)))`,
          }}
        />
      )}
      {children}
    </div>
  );
};

const emptyStateStyle = {
  padding: '40px 18px',
  textAlign: 'center',
  color: 'var(--faint)',
  fontSize: 12.5,
};

export const EmptyState = ({ children }) => (
  <div className="syn-empty" style={emptyStateStyle}>
    {children}
  </div>
);

const listStyles = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '2px 8px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

/**
 * Scrolling body every sidebar puts its rows in.
 *
 * Extra props are forwarded to the scrolling div. `data-infinite-root` is the
 * one that matters: `useInfiniteList` finds its observer root with
 * `node.closest('[data-infinite-root]')`, so without the spread the attribute
 * never reached the DOM and every list that set it silently paged against the
 * viewport instead of its own scroll container.
 *
 * Forwards its ref so a list can hand the scrolling element to an
 * IntersectionObserver as the `root`. Paging off `onScroll` alone cannot see the
 * case that matters: a list too short to scroll fires no scroll event, so it can
 * never ask for the page that would make it longer.
 */
export const ListBody = forwardRef(function ListBody({ children, onScroll, ...rest }, ref) {
  return (
    <div ref={ref} className="custom-scrollbar" onScroll={onScroll} style={listStyles} {...rest}>
      {children}
    </div>
  );
});

const panelStyle = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: 'var(--panel)',
};

/** Outer frame of a sidebar: full height, column, its own scroll context. */
export const SidebarPanel = ({ children }) => <div style={panelStyle}>{children}</div>;
